import "./style.scss";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import DownloadSvg from "@/src/svg/applicationsPage/DownloadSvg";
import SelectDefault from "../../SelectDefault/SelectDefault";
import { FC } from "react";
import ArrowBottom from "@/src/svg/default/ArrowBottom";
import VerticalLine from "../../VerticalLine/VerticalLine";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import { setFilterAndSearch } from "@/src/store/slices/AdmissionOfficeDatabase/AODatabaseTableSlice";
import * as XLSX from "xlsx";
import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { admissionDatabaseDefaultBodyFilters } from "@/src/constants/AdmissionOffice/admissionOfficeDatabaseConsts";
import { IADatabaseDocument, IADatabaseSearchAndFilters } from "@/src/types/admissionDatabase";

const keysOrder = [
  "data_added",
  "full_name",
  "document_type",
  "document_seria",
  "date_of_birth",
  "nationality",
  "course",
  "whose_doc",
];

interface IAPFooter {
  isCurrentPage: string;
  setIsCurrentPage: React.Dispatch<React.SetStateAction<string>>;
  isSelectPagination: string;
  setIsSelectPagination: React.Dispatch<React.SetStateAction<string>>;
  actualQuantityRows: number;
}

const AODFooter: FC<IAPFooter> = ({
  isCurrentPage,
  setIsCurrentPage,
  isSelectPagination,
  setIsSelectPagination,
  actualQuantityRows,
}) => {
  const dispatch = useDispatch();

  const handleChangePagination = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setIsCurrentPage("1");
    setIsSelectPagination(event.target.value);
  };

  const calcActualPage = (number: number) => {
    const newPage = Number(isCurrentPage) + number;
    if (newPage > 0 && newPage <= Math.ceil(actualQuantityRows / Number(isSelectPagination))) {
      setIsCurrentPage(newPage.toString());
    }
  };

  const bodyFilters = useSelector((state: RootState) => state.AODatabaseTable.filterAndSearch);

  const totalPages = Math.ceil(actualQuantityRows / Number(isSelectPagination));
  const currentPage = Number(isCurrentPage);

  const getPagesToShow = () => {
    const pages: number[] = [];
    const totalPages = Math.ceil(actualQuantityRows / Number(isSelectPagination));
    const currentPage = Number(isCurrentPage);

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage <= 3) {
        pages.push(2, 3, 4);
      } else if (currentPage >= totalPages - 2) {
        pages.push(totalPages - 3, totalPages - 2, totalPages - 1);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
      pages.push(totalPages);
    }

    return Array.from(new Set(pages)).sort((a, b) => a - b);
  };

  const pagesToShow = getPagesToShow();

  const checkFiltersActive = () => {
    return Object.keys(admissionDatabaseDefaultBodyFilters)
      .filter((key) => key !== "page")
      .some((key) => {
        const k = key as keyof IADatabaseSearchAndFilters;
        const initialValue = admissionDatabaseDefaultBodyFilters[k];
        const currentValue = bodyFilters[k];

        if (Array.isArray(initialValue)) {
          return (currentValue as string[]).length > 0;
        } else {
          return currentValue !== initialValue && currentValue !== "";
        }
      });
  };

  const handleDownloadAll = async () => {
    try {
      const filtersActive = checkFiltersActive();

      // 1. Загружаем первую страницу
      let firstPage;

      if (filtersActive) {
        firstPage = await admissionDatabaseRequests.filterAndSearch({
          ...bodyFilters,
          page: 1,
        });
      } else {
        firstPage = await admissionDatabaseRequests.getListDefault("1");
      }

      if (!firstPage?.docsArray) {
        alert("Ошибка: нет данных");
        return;
      }

      const total = firstPage.total;
      const pageSize = Number(isSelectPagination); // 50
      const totalPages = Math.ceil(total / pageSize);

      let allRows: IADatabaseDocument[] = [...firstPage.docsArray];

      // 2. Загружаем остальные страницы
      for (let page = 2; page <= totalPages; page++) {
        let res;

        if (filtersActive) {
          res = await admissionDatabaseRequests.filterAndSearch({
            ...bodyFilters,
            page: page,
          });
        } else {
          res = await admissionDatabaseRequests.getListDefault(page.toString());
        }

        if (res?.docsArray) {
          allRows = [...allRows, ...res.docsArray];
        }
      }

      console.log("ALL ROWS:", allRows);

      // === 3. Сортируем по нужным колонкам ===
      const orderedRows = allRows.map((row) => {
        const newObj: any = {};
        keysOrder.forEach((key) => {
          newObj[key] = row[key as keyof IADatabaseDocument] ?? "";
        });
        return newObj;
      });

      // === 4. Excel ===
      const worksheet = XLSX.utils.json_to_sheet(orderedRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

      XLSX.writeFile(workbook, "admission-database.xlsx");
    } catch (error) {
      console.error(error);
      alert("Ошибка при скачивании");
    }
  };

  return (
    <div className="admission-database-footer">
      <span className="pag-info">
        Showing {Number(isSelectPagination) * (currentPage - 1) + 1} -{" "}
        {Number(isSelectPagination) * currentPage > actualQuantityRows
          ? actualQuantityRows
          : Number(isSelectPagination) * currentPage}{" "}
        out of {actualQuantityRows}
      </span>
      <div className="admission-database-footer-controllers">
        <div className="pagination">
          <button
            className="pagination-btn prev"
            onClick={() => calcActualPage(-1)}
            disabled={currentPage === 1}
          >
            <ArrowBottom />
          </button>

          {pagesToShow.map((page) => (
            <button
              key={page}
              className={page === currentPage ? "active" : ""}
              onClick={() => setIsCurrentPage(page.toString())}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn next"
            onClick={() => calcActualPage(1)}
            disabled={currentPage === totalPages}
          >
            <ArrowBottom />
          </button>
        </div>

        <div className="pagination-select">
          <SelectDefault
            onChange={handleChangePagination}
            value={isSelectPagination}
            reverse={true}
          >
            <option>50</option>
          </SelectDefault>
        </div>
        <VerticalLine />
        <BtnDefault onClick={handleDownloadAll}>
          <p>Download</p>
          <DownloadSvg />
        </BtnDefault>
      </div>
    </div>
  );
};

export default AODFooter;
